import React from 'react';
import { Alert, Box, Chip } from '@mui/material';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Button } from '../../../components/library';
import { WarehouseBinAutocomplete } from '../../../components/inventory/WarehouseBinAutocomplete';
import { WarehouseBin } from '../../../services/inventory/types';
import {
  FulfillmentLineExceptionType,
  FulfillmentLineStatus,
  LineItem,
  LineItemLocalState,
} from '../../../services/fulfillment/types';
import { EXCEPTION_TYPES, LINE_STATUSES } from '../../../services/fulfillment/constants';
import { toBinOption } from '../FulfillmentOrderDetail.utils';

interface Props {
  item: LineItem;
  ls: LineItemLocalState;
  inWave: boolean;
  saving: boolean;
  qcZoneTypes: string[];
  binWarning: string | undefined;
  onClearWarning: () => void;
  onStatusChange: (newStatus: FulfillmentLineStatus) => void;
  onPatchLocalState: (patch: Partial<LineItemLocalState>) => void;
  onSave: (payload: {
    status: FulfillmentLineStatus;
    fulfillmentBinCode?: string;
    exceptionType?: FulfillmentLineExceptionType;
    notes?: string;
  }) => void;
}

export const LineStatusEditor: React.FC<Props> = ({
  item,
  ls,
  inWave,
  saving,
  qcZoneTypes,
  binWarning,
  onClearWarning,
  onStatusChange,
  onPatchLocalState,
  onSave,
}) => {
  if (ls.status === 'removed') {
    return (
      <Chip
        label="Removed"
        size="small"
        sx={{ color: 'grey.500', borderColor: 'grey.400' }}
        variant="outlined"
      />
    );
  }

  const selectedStatus = ls.pendingStatus ?? ls.status;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Autocomplete<FulfillmentLineStatus>
        options={LINE_STATUSES}
        value={selectedStatus}
        onChange={(_, newVal) => {
          if (newVal) onStatusChange(newVal);
        }}
        getOptionLabel={s => s}
        getOptionSelected={(opt, val) => opt === val}
        disabled={inWave || saving}
        renderInput={params => <TextField {...params} variant="outlined" size="small" />}
      />

      {ls.pendingStatus === 'quality-check' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <WarehouseBinAutocomplete
            options={item.binLocations
              .filter(b => qcZoneTypes.includes(b.locationType as string))
              .map(b => toBinOption({ id: b.id, code: b.binCode }))}
            selectedBin={ls.pendingBin ? toBinOption(ls.pendingBin) : undefined}
            typeaheadVal={ls.pendingBinTypeahead}
            onTypeaheadChange={(val: string) => onPatchLocalState({ pendingBinTypeahead: val })}
            onBinChange={(bin: WarehouseBin | undefined) =>
              onPatchLocalState({ pendingBin: bin ? { id: bin.id, code: bin.code } : null })
            }
          />
          <Button
            ordinality="primary"
            size="small"
            disabled={!ls.pendingBin || saving}
            onClick={() => {
              if (ls.pendingBin) {
                onSave({ status: 'quality-check', fulfillmentBinCode: ls.pendingBin.code });
              }
            }}
          >
            Save
          </Button>
        </Box>
      )}

      {ls.pendingStatus &&
        (ls.pendingStatus === 'picked' || ls.pendingStatus === 'packed') &&
        (() => {
          const fulfillmentOptions = item.binLocations
            .filter(b => b.locationType === 'fulfillment')
            .map(b => toBinOption({ id: b.id, code: b.binCode }));
          const noInventory = fulfillmentOptions.length === 0;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {noInventory ? (
                <Alert severity="error" sx={{ fontSize: 12, py: 0 }}>
                  No inventory found in a fulfillment zone. Please relocate the item first.
                </Alert>
              ) : (
                <WarehouseBinAutocomplete
                  options={fulfillmentOptions}
                  selectedBin={ls.pendingBin ? toBinOption(ls.pendingBin) : undefined}
                  typeaheadVal={ls.pendingBinTypeahead}
                  onTypeaheadChange={(val: string) =>
                    onPatchLocalState({ pendingBinTypeahead: val })
                  }
                  onBinChange={(bin: WarehouseBin | undefined) =>
                    onPatchLocalState({ pendingBin: bin ? { id: bin.id, code: bin.code } : null })
                  }
                />
              )}
              <Button
                ordinality="primary"
                size="small"
                disabled={noInventory || !ls.pendingBin || saving}
                onClick={() => {
                  if (ls.pendingStatus && ls.pendingBin) {
                    onSave({
                      status: ls.pendingStatus,
                      fulfillmentBinCode: ls.pendingBin.code,
                    });
                  }
                }}
              >
                Save
              </Button>
            </Box>
          );
        })()}

      {ls.pendingStatus === 'exception' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Autocomplete<{ value: FulfillmentLineExceptionType; label: string }>
            options={EXCEPTION_TYPES}
            value={EXCEPTION_TYPES.find(e => e.value === ls.pendingExceptionType) ?? null}
            onChange={(_, newVal) =>
              onPatchLocalState({ pendingExceptionType: newVal?.value ?? null })
            }
            getOptionLabel={et => et.label}
            getOptionSelected={(opt, val) => opt.value === val.value}
            disabled={saving}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder="Exception type…"
              />
            )}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Notes (optional)"
            value={ls.pendingNotes ?? ''}
            disabled={saving}
            onChange={e => onPatchLocalState({ pendingNotes: e.target.value || null })}
            inputProps={{ style: { fontSize: 13 } }}
          />
          <Button
            ordinality="primary"
            size="small"
            disabled={!ls.pendingExceptionType || saving}
            onClick={() => {
              if (ls.pendingExceptionType) {
                onSave({
                  status: 'exception',
                  exceptionType: ls.pendingExceptionType,
                  notes: ls.pendingNotes ?? undefined,
                });
              }
            }}
          >
            Save
          </Button>
        </Box>
      )}

      {binWarning && (
        <Alert severity="warning" sx={{ fontSize: 12, py: 0 }} onClose={onClearWarning}>
          {binWarning}
        </Alert>
      )}
    </Box>
  );
};
